// src/pages/ActivationPage.js - VERSION ADAPTÉE À LA CHARTE
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const ActivationPage = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [users, setUsers] = useState([]);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRejected, setLoadingRejected] = useState(false);
  const [error, setError] = useState(null);
  const [infoMsg, setInfoMsg] = useState("");
  const [activating, setActivating] = useState({});
  const [rejecting, setRejecting] = useState({});
  const [resetting, setResetting] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // États pour les modals
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  const navigate = useNavigate();

  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Vérifier si admin
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (Number(userData.profile) !== 1) {
          setError("Accès restreint - Administrateur seulement");
          setTimeout(() => navigate('/dashboard'), 2000);
        }
      } catch (e) {
        console.error("Erreur parsing user:", e);
      }
    }
  }, [navigate]);

  // Fonction pour charger les utilisateurs en attente
  const fetchPendingUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInfoMsg("");
    
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/users/activation.php`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.data.success) {
        const usersData = response.data.data.users || [];
        setUsers(usersData);
        
        if (usersData.length === 0 && activeTab === 'pending') {
          setInfoMsg("Aucun utilisateur en attente d'activation.");
        }
      } else {
        setError(response.data.message || 'Erreur lors du chargement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      
      if (error.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        setTimeout(() => navigate('/login'), 2000);
      } else if (error.response?.status === 403) {
        setError('Accès non autorisé. Administrateur seulement.');
      } else if (!error.response) {
        setError('Serveur indisponible.');
      } else {
        setError('Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, activeTab]);

  // Fonction pour charger les utilisateurs rejetés
  const fetchRejectedUsers = useCallback(async () => {
    setLoadingRejected(true);
    
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/users/activation.php?status=rejected`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.data.success) {
        const usersData = response.data.data.users || [];
        setRejectedUsers(usersData);
        
        if (usersData.length === 0 && activeTab === 'rejected') {
          setInfoMsg("Aucun compte rejeté trouvé.");
        }
      }
    } catch (error) {
      console.error('Erreur chargement rejetés:', error);
    } finally {
      setLoadingRejected(false);
    }
  }, []);

  // Charger les données selon l'onglet actif
  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingUsers();
    } else if (activeTab === 'rejected') {
      fetchRejectedUsers();
    }
  }, [activeTab, fetchPendingUsers, fetchRejectedUsers]);

  // Fonction pour activer un utilisateur
  const handleActivateUser = async (userId, userName) => {
    if (!window.confirm(`Activer le compte de ${userName} ?`)) {
      return;
    }

    setActivating(prev => ({ ...prev, [userId]: true }));

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${API_BASE_URL}/users/activation.php`,
        { user_id: userId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        fetchPendingUsers();
        fetchRejectedUsers();
        
        let message = `✅ Compte de ${userName} activé avec succès !`;
        if (response.data.email_sent === false) {
          message += "\n⚠️ Note: L'email de confirmation n'a pas pu être envoyé.";
        }
        
        setInfoMsg(message);
        setTimeout(() => setInfoMsg(""), 5000);
      } else {
        setError(`❌ Erreur: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Erreur activation:', error);
      setError('Erreur lors de l\'activation');
    } finally {
      setActivating(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Fonction pour rejeter un utilisateur
  const handleRejectUser = async (userId, userName) => {
    if (!rejectReason.trim()) {
      alert("Veuillez indiquer un motif de rejet.");
      return;
    }

    if (!window.confirm(`Rejeter le compte de ${userName} ?\nMotif: ${rejectReason}`)) {
      return;
    }

    setRejecting(prev => ({ ...prev, [userId]: true }));

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${API_BASE_URL}/users/activation.php?action=reject`,
        { 
          user_id: userId,
          reason: rejectReason
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        fetchPendingUsers();
        fetchRejectedUsers();
        
        let message = `❌ Compte de ${userName} rejeté avec succès.`;
        if (response.data.email_sent === false) {
          message += "\n⚠️ Note: L'email de notification n'a pas pu être envoyé.";
        }
        
        setInfoMsg(message);
        setTimeout(() => setInfoMsg(""), 5000);
        
        setShowRejectModal(false);
        setRejectReason('');
        setSelectedUser(null);
        setActiveTab('rejected');
      } else {
        setError(`❌ Erreur: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Erreur rejet:', error);
      setError('Erreur lors du rejet');
    } finally {
      setRejecting(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Fonction pour réinitialiser un rejet
  const handleResetRejection = async (userId, userName) => {
    if (!window.confirm(`Réinitialiser le rejet de ${userName} ?\nL'utilisateur sera remis en attente d'activation.`)) {
      return;
    }

    setResetting(prev => ({ ...prev, [userId]: true }));

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${API_BASE_URL}/users/activation.php?action=reset`,
        { 
          user_id: userId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        fetchPendingUsers();
        fetchRejectedUsers();
        
        setInfoMsg(`🔄 Compte de ${userName} remis en attente d'activation.`);
        setTimeout(() => setInfoMsg(""), 5000);
        setActiveTab('pending');
      } else {
        setError(`❌ Erreur: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Erreur réinitialisation:', error);
      setError('Erreur lors de la réinitialisation');
    } finally {
      setResetting(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Fonction pour obtenir le nom du fichier sans chemin
  const getFileName = (filepath) => {
    if (!filepath) return null;
    const parts = filepath.split('/');
    return parts[parts.length - 1];
  };

  // Ouvrir la modal des documents
  const openDocumentsModal = (user) => {
    setSelectedUser(user);
    setShowDocumentsModal(true);
  };

  // Ouvrir la modal de rejet
  const openRejectModal = (user) => {
    setSelectedUser(user);
    setRejectReason('');
    setShowRejectModal(true);
  };

  // Gérer la recherche
  const handleSearch = (e) => {
    e.preventDefault();
  };

  // Filtrer les utilisateurs selon la recherche
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.nom?.toLowerCase().includes(searchLower) ||
      user.prenom?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.societe?.nom?.toLowerCase().includes(searchLower) ||
      user.societe?.email?.toLowerCase().includes(searchLower)
    );
  });

  const filteredRejectedUsers = rejectedUsers.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.nom?.toLowerCase().includes(searchLower) ||
      user.prenom?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.societe?.nom?.toLowerCase().includes(searchLower) ||
      user.societe?.email?.toLowerCase().includes(searchLower)
    );
  });

  // Fonction pour afficher le statut de validation
  const getValidationStatus = (user) => {
    if (user.societe?.statut_validation === 'rejetee') {
      return {
        text: 'Rejeté',
        badge: 'danger',
        icon: 'bi-x-circle'
      };
    } else if (user.societe?.statut_validation === 'validee') {
      return {
        text: 'Validé',
        badge: 'success',
        icon: 'bi-check-circle'
      };
    } else {
      return {
        text: 'En attente',
        badge: 'warning',
        icon: 'bi-clock'
      };
    }
  };

  // STYLE CSS pour les boutons
  const buttonStyles = {
    iconButton: {
      border: 'none',
      background: 'transparent',
      fontSize: '1.2rem',
      padding: '5px 8px',
      borderRadius: '6px',
      cursor: 'pointer',
      margin: '0 3px',
      transition: 'all 0.2s ease'
    }
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des Inscriptions</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Administration</li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Inscriptions</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="card-title mb-0" style={{ 
                color: COLORS.primary,
                display: 'flex',
                alignItems: 'center'
              }}>
                <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#E3F2FD',
                  color: COLORS.primary
                }}>
                  <i className="bi bi-person-check"></i>
                </div>
                Validation des comptes
              </h5>
              
              <div className="d-flex gap-2">
                <button 
                  className="btn btn-sm fw-semibold"
                  onClick={() => activeTab === 'pending' ? fetchPendingUsers() : fetchRejectedUsers()}
                  title="Actualiser"
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </div>

            {/* Onglets */}
            <ul className="nav nav-tabs mb-4" id="activationTabs">
              <li className="nav-item">
                <button 
                  className={`nav-link fw-semibold ${activeTab === 'pending' ? 'active' : ''}`}
                  onClick={() => setActiveTab('pending')}
                  style={{
                    borderRadius: '8px 8px 0 0',
                    borderColor: activeTab === 'pending' ? COLORS.primary : '#dee2e6',
                    backgroundColor: activeTab === 'pending' ? COLORS.primary : 'transparent',
                    color: activeTab === 'pending' ? 'white' : COLORS.darkText,
                    marginRight: '4px'
                  }}
                >
                  <i className="bi bi-clock me-1"></i>
                  En attente
                  {users.length > 0 && (
                    <span className="badge ms-2" style={{ 
                      backgroundColor: activeTab === 'pending' ? 'white' : COLORS.warning,
                      color: activeTab === 'pending' ? COLORS.primary : 'white'
                    }}>{users.length}</span>
                  )}
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link fw-semibold ${activeTab === 'rejected' ? 'active' : ''}`}
                  onClick={() => setActiveTab('rejected')}
                  style={{
                    borderRadius: '8px 8px 0 0',
                    borderColor: activeTab === 'rejected' ? COLORS.danger : '#dee2e6',
                    backgroundColor: activeTab === 'rejected' ? COLORS.danger : 'transparent',
                    color: activeTab === 'rejected' ? 'white' : COLORS.darkText
                  }}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Rejetés
                  {rejectedUsers.length > 0 && (
                    <span className="badge ms-2" style={{ 
                      backgroundColor: activeTab === 'rejected' ? 'white' : '#ffebee',
                      color: activeTab === 'rejected' ? COLORS.danger : '#c62828'
                    }}>{rejectedUsers.length}</span>
                  )}
                </button>
              </li>
            </ul>

            {/* Filtres */}
            <div className="row mb-4">
              <div className="col-md-12">
                <form onSubmit={handleSearch} className="row g-2">
                  <div className="col-md-8">
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: '#e0e0e0',
                        color: COLORS.secondary
                      }}>
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Rechercher ${activeTab === 'pending' ? 'en attente' : 'rejeté'}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          borderColor: '#e0e0e0',
                          color: COLORS.darkText
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <button type="submit" className="btn btn-sm fw-semibold w-100" style={{
                      color: 'white',
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                    }}>
                      <i className="bi bi-search me-1"></i> Rechercher
                    </button>
                  </div>
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="btn btn-sm fw-semibold w-100"
                      onClick={() => setSearchTerm("")}
                      style={{
                        color: COLORS.darkText,
                        borderColor: '#e0e0e0',
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.lightBg;
                        e.target.style.borderColor = COLORS.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = '#e0e0e0';
                      }}
                    >
                      <i className="bi bi-x-lg"></i> Effacer
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Messages d'erreur/succès */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.danger
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
                <div className="mt-2">
                  <button 
                    className="btn btn-sm fw-semibold"
                    onClick={() => activeTab === 'pending' ? fetchPendingUsers() : fetchRejectedUsers()}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      padding: '4px 12px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i> Réessayer
                  </button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.success
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                  <div className="flex-grow-1">{infoMsg}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setInfoMsg("")}
                  ></button>
                </div>
              </div>
            )}

            {/* CONTENU ONGLET "EN ATTENTE" */}
            {activeTab === 'pending' && (
              <>
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des utilisateurs en attente...</p>
                  </div>
                ) : (
                  <>
                    {filteredUsers.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                          width: '80px',
                          height: '80px',
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-people fs-3"></i>
                        </div>
                        <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Aucun utilisateur en attente d'activation</h5>
                        <p style={{ color: COLORS.darkText }}>Tous les comptes sont activés ou en cours de validation.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead>
                            <tr style={{ backgroundColor: COLORS.lightBg }}>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Utilisateur</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Contact</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Société</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Inscription</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>État</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Documents</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none', width: '120px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredUsers.map((user) => {
                              const status = getValidationStatus(user);
                              return (
                                <tr key={user.id} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                                  <td style={{ padding: '16px 8px' }}>
                                    <div className="d-flex align-items-center">
                                      <div className="me-3">
                                        <div className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                          style={{ 
                                            width: '40px', 
                                            height: '40px',
                                            backgroundColor: COLORS.primary,
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            boxShadow: '0 2px 4px rgba(11, 60, 93, 0.2)'
                                          }}>
                                          {user.prenom?.charAt(0) || ''}{user.nom?.charAt(0) || ''}
                                        </div>
                                      </div>
                                      <div>
                                        <strong style={{ color: COLORS.darkText, display: 'block' }}>{user.prenom} {user.nom}</strong>
                                        <div className="small" style={{ color: '#757575' }}>
                                          ID: {user.id}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{ padding: '16px 8px', color: COLORS.darkText }}>
                                    <div>
                                      <div className="d-flex align-items-center mb-1">
                                        <i className="bi bi-envelope me-2" style={{ color: COLORS.secondary, fontSize: '0.875rem' }}></i>
                                        <small style={{ fontSize: '0.875rem' }}>{user.email}</small>
                                      </div>
                                      <div className="d-flex align-items-center">
                                        <i className="bi bi-telephone me-2" style={{ color: COLORS.secondary, fontSize: '0.875rem' }}></i>
                                        <small style={{ fontSize: '0.875rem' }}>{user.phone || '-'}</small>
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{ padding: '16px 8px', color: COLORS.darkText }}>
                                    <div>
                                      <div className="d-flex align-items-center mb-1">
                                        <i className="bi bi-building me-2" style={{ color: COLORS.secondary, fontSize: '0.875rem' }}></i>
                                        <strong style={{ fontSize: '0.875rem' }}>{user.societe?.nom || '-'}</strong>
                                      </div>
                                      {user.societe?.email && (
                                        <small className="d-flex align-items-center" style={{ fontSize: '0.75rem', color: '#757575' }}>
                                          <i className="bi bi-envelope me-1"></i>
                                          {user.societe.email}
                                        </small>
                                      )}
                                    </div>
                                  </td>
                                  <td style={{ padding: '16px 8px' }}>
                                    <small style={{ color: COLORS.darkText, display: 'block' }}>
                                      {new Date(user.created_at).toLocaleDateString('fr-FR')}
                                    </small>
                                    <span className="badge" style={{ 
                                      backgroundColor: COLORS.lightBg,
                                      color: COLORS.darkText,
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                      fontWeight: '500'
                                    }}>
                                      {new Date(user.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </td>
                                  <td style={{ padding: '16px 8px' }}>
                                    <div className="d-flex flex-column gap-1">
                                      <span className="badge" style={{ 
                                        backgroundColor: user.email_confirme === "1" ? '#e8f5e9' : '#f5f5f5',
                                        color: user.email_confirme === "1" ? '#2e7d32' : '#757575',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: '500'
                                      }}>
                                        <i className={`bi ${user.email_confirme === "1" ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                                        Email {user.email_confirme === "1" ? '✓' : '✗'}
                                      </span>
                                      <span className="badge" style={{ 
                                        backgroundColor: status.badge === 'success' ? '#e8f5e9' : 
                                                       status.badge === 'warning' ? '#fff8e1' : '#ffebee',
                                        color: status.badge === 'success' ? '#2e7d32' : 
                                               status.badge === 'warning' ? '#ff8f00' : '#c62828',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: '500'
                                      }}>
                                        <i className={`bi ${status.icon} me-1`}></i>
                                        {status.text}
                                      </span>
                                    </div>
                                  </td>
                                  <td style={{ padding: '16px 8px' }}>
                                    <div className="d-flex flex-wrap gap-1">
                                      {user.societe?.chemin_nif && (
                                        <span 
                                          className="badge"
                                          style={{ 
                                            backgroundColor: '#E3F2FD',
                                            color: COLORS.primary,
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            fontSize: '0.75rem',
                                            fontWeight: '500'
                                          }}
                                          title="NIF fourni"
                                        >
                                          <i className="bi bi-file-earmark-text me-1"></i>
                                          NIF
                                        </span>
                                      )}
                                      {user.societe?.chemin_rccm && (
                                        <span 
                                          className="badge"
                                          style={{ 
                                            backgroundColor: '#E3F2FD',
                                            color: COLORS.primary,
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            fontSize: '0.75rem',
                                            fontWeight: '500'
                                          }}
                                          title="RCCM fourni"
                                        >
                                          <i className="bi bi-file-earmark-text me-1"></i>
                                          RCCM
                                        </span>
                                      )}
                                      {!user.societe?.chemin_nif && !user.societe?.chemin_rccm && (
                                        <span className="badge" style={{ 
                                          backgroundColor: COLORS.lightBg,
                                          color: COLORS.darkText,
                                          padding: '4px 8px',
                                          borderRadius: '6px',
                                          fontSize: '0.75rem',
                                          fontWeight: '500'
                                        }}>
                                          Aucun document
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td style={{ padding: '16px 8px' }}>
                                    <div className="d-flex justify-content-start">
                                      {/* Bouton Voir documents */}
                                      <button
                                        style={{
                                          ...buttonStyles.iconButton,
                                          backgroundColor: '#E3F2FD',
                                          color: COLORS.primary
                                        }}
                                        onClick={() => openDocumentsModal(user)}
                                        title="Voir documents"
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor = COLORS.primary;
                                          e.target.style.color = 'white';
                                          e.target.style.transform = 'translateY(-2px)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = '#E3F2FD';
                                          e.target.style.color = COLORS.primary;
                                          e.target.style.transform = 'translateY(0)';
                                        }}
                                      >
                                        <i className="bi bi-folder"></i>
                                      </button>
                                      
                                      {/* Bouton Activer */}
                                      <button
                                        style={{
                                          ...buttonStyles.iconButton,
                                          backgroundColor: '#e8f5e9',
                                          color: '#2e7d32'
                                        }}
                                        onClick={() => handleActivateUser(user.id, `${user.prenom} ${user.nom}`)}
                                        disabled={activating[user.id] || user.compte_actif === "1"}
                                        title={user.compte_actif === "1" ? "Déjà activé" : "Activer le compte"}
                                        onMouseEnter={(e) => {
                                          if (!activating[user.id] && user.compte_actif !== "1") {
                                            e.target.style.backgroundColor = '#2e7d32';
                                            e.target.style.color = 'white';
                                            e.target.style.transform = 'translateY(-2px)';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = '#e8f5e9';
                                          e.target.style.color = '#2e7d32';
                                          e.target.style.transform = 'translateY(0)';
                                        }}
                                      >
                                        {activating[user.id] ? (
                                          <span className="spinner-border spinner-border-sm" style={{ width: '1rem', height: '1rem' }}></span>
                                        ) : (
                                          <i className={`bi ${user.compte_actif === "1" ? 'bi-check-circle' : 'bi-person-check'}`}></i>
                                        )}
                                      </button>
                                      
                                      {/* Bouton Rejeter */}
                                      <button
                                        style={{
                                          ...buttonStyles.iconButton,
                                          backgroundColor: '#ffebee',
                                          color: '#c62828'
                                        }}
                                        onClick={() => openRejectModal(user)}
                                        disabled={user.compte_actif === "1"}
                                        title="Rejeter le compte"
                                        onMouseEnter={(e) => {
                                          if (user.compte_actif !== "1") {
                                            e.target.style.backgroundColor = '#c62828';
                                            e.target.style.color = 'white';
                                            e.target.style.transform = 'translateY(-2px)';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = '#ffebee';
                                          e.target.style.color = '#c62828';
                                          e.target.style.transform = 'translateY(0)';
                                        }}
                                      >
                                        <i className="bi bi-x-circle"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* CONTENU ONGLET "REJETES" */}
            {activeTab === 'rejected' && (
              <>
                {loadingRejected ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des comptes rejetés...</p>
                  </div>
                ) : (
                  <>
                    {filteredRejectedUsers.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                          width: '80px',
                          height: '80px',
                          backgroundColor: '#ffebee',
                          color: '#c62828'
                        }}>
                          <i className="bi bi-person-x fs-3"></i>
                        </div>
                        <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Aucun compte rejeté</h5>
                        <p style={{ color: COLORS.darkText }}>Tous les comptes sont en attente ou activés.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead>
                            <tr style={{ backgroundColor: COLORS.lightBg }}>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Utilisateur</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Contact</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Société</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Date rejet</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Motif</th>
                              <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none', width: '80px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRejectedUsers.map((user) => (
                              <tr key={user.id} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                                <td style={{ padding: '16px 8px' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                        style={{ 
                                          width: '40px', 
                                          height: '40px',
                                          backgroundColor: '#c62828',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          boxShadow: '0 2px 4px rgba(198, 40, 40, 0.2)'
                                        }}>
                                        {user.prenom?.charAt(0) || ''}{user.nom?.charAt(0) || ''}
                                      </div>
                                    </div>
                                    <div>
                                      <strong style={{ color: COLORS.darkText, display: 'block' }}>{user.prenom} {user.nom}</strong>
                                      <div className="small" style={{ color: '#757575' }}>
                                        ID: {user.id}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ padding: '16px 8px', color: COLORS.darkText }}>
                                  <div>
                                    <div className="d-flex align-items-center mb-1">
                                      <i className="bi bi-envelope me-2" style={{ color: COLORS.secondary, fontSize: '0.875rem' }}></i>
                                      <small style={{ fontSize: '0.875rem' }}>{user.email}</small>
                                    </div>
                                    <div className="d-flex align-items-center">
                                      <i className="bi bi-telephone me-2" style={{ color: COLORS.secondary, fontSize: '0.875rem' }}></i>
                                      <small style={{ fontSize: '0.875rem' }}>{user.phone || '-'}</small>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ padding: '16px 8px', color: COLORS.darkText }}>
                                  <div>
                                    <div className="d-flex align-items-center mb-1">
                                      <i className="bi bi-building me-2" style={{ color: COLORS.secondary, fontSize: '0.875rem' }}></i>
                                      <strong style={{ fontSize: '0.875rem' }}>{user.societe?.nom || '-'}</strong>
                                    </div>
                                    {user.societe?.email && (
                                      <small className="d-flex align-items-center" style={{ fontSize: '0.75rem', color: '#757575' }}>
                                        <i className="bi bi-envelope me-1"></i>
                                        {user.societe.email}
                                      </small>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: '16px 8px' }}>
                                  <small style={{ color: COLORS.darkText, display: 'block' }}>
                                    {user.societe?.date_validation ? 
                                      new Date(user.societe.date_validation).toLocaleDateString('fr-FR') : 
                                      'Non spécifiée'}
                                  </small>
                                  {user.societe?.date_validation && (
                                    <span className="badge" style={{ 
                                      backgroundColor: COLORS.lightBg,
                                      color: COLORS.darkText,
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                      fontWeight: '500'
                                    }}>
                                      {new Date(user.societe.date_validation).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '16px 8px' }}>
                                  <small style={{ color: COLORS.darkText }} title={user.societe?.motif_rejet || 'Non spécifié'}>
                                    {user.societe?.motif_rejet ? 
                                      (user.societe.motif_rejet.length > 50 ? 
                                        user.societe.motif_rejet.substring(0, 50) + '...' : 
                                        user.societe.motif_rejet) : 
                                      'Non spécifié'}
                                  </small>
                                </td>
                                <td style={{ padding: '16px 8px' }}>
                                  <div className="d-flex justify-content-start">
                                    {/* Bouton Voir documents */}
                                    <button
                                      style={{
                                        ...buttonStyles.iconButton,
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.primary
                                      }}
                                      onClick={() => openDocumentsModal(user)}
                                      title="Voir documents"
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = COLORS.primary;
                                        e.target.style.color = 'white';
                                        e.target.style.transform = 'translateY(-2px)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#E3F2FD';
                                        e.target.style.color = COLORS.primary;
                                        e.target.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      <i className="bi bi-folder"></i>
                                    </button>
                                    
                                    {/* Bouton Réinitialiser */}
                                    <button
                                      style={{
                                        ...buttonStyles.iconButton,
                                        backgroundColor: '#fff8e1',
                                        color: '#ff8f00'
                                      }}
                                      onClick={() => handleResetRejection(user.id, `${user.prenom} ${user.nom}`)}
                                      disabled={resetting[user.id]}
                                      title="Remettre en attente"
                                      onMouseEnter={(e) => {
                                        if (!resetting[user.id]) {
                                          e.target.style.backgroundColor = '#ff8f00';
                                          e.target.style.color = 'white';
                                          e.target.style.transform = 'translateY(-2px)';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#fff8e1';
                                        e.target.style.color = '#ff8f00';
                                        e.target.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      {resetting[user.id] ? (
                                        <span className="spinner-border spinner-border-sm" style={{ width: '1rem', height: '1rem' }}></span>
                                      ) : (
                                        <i className="bi bi-arrow-clockwise"></i>
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Modal pour afficher les documents */}
      {showDocumentsModal && selectedUser && selectedUser.societe && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header" style={{ 
                backgroundColor: COLORS.lightBg,
                borderBottom: `1px solid ${COLORS.lightBg}`,
                borderRadius: '12px 12px 0 0'
              }}>
                <h5 className="modal-title" style={{ color: COLORS.primary, fontWeight: '600' }}>
                  <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                    width: '30px',
                    height: '30px',
                    backgroundColor: COLORS.primary,
                    color: 'white'
                  }}>
                    <i className="bi bi-file-earmark-text"></i>
                  </div>
                  Documents - {selectedUser.societe.nom}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowDocumentsModal(false);
                    setSelectedUser(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card mb-3" style={{ 
                      border: `1px solid ${COLORS.lightBg}`,
                      borderRadius: '8px'
                    }}>
                      <div className="card-body">
                        <h6 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Informations Société</h6>
                        <table className="table table-sm table-borderless mb-0">
                          <tbody>
                            <tr>
                              <td width="40%" style={{ fontWeight: '500', color: COLORS.darkText }}>Nom:</td>
                              <td style={{ color: COLORS.darkText }}>{selectedUser.societe.nom}</td>
                            </tr>
                            <tr>
                              <td style={{ fontWeight: '500', color: COLORS.darkText }}>Email:</td>
                              <td style={{ color: COLORS.darkText }}>{selectedUser.societe.email}</td>
                            </tr>
                            <tr>
                              <td style={{ fontWeight: '500', color: COLORS.darkText }}>Adresse:</td>
                              <td style={{ color: COLORS.darkText }}>{selectedUser.societe.address || 'Non spécifié'}</td>
                            </tr>
                            <tr>
                              <td style={{ fontWeight: '500', color: COLORS.darkText }}>Téléphone:</td>
                              <td style={{ color: COLORS.darkText }}>{selectedUser.societe.telephone || '-'}</td>
                            </tr>
                            <tr>
                              <td style={{ fontWeight: '500', color: COLORS.darkText }}>NIF:</td>
                              <td style={{ color: COLORS.darkText }}>{selectedUser.societe.nif || '-'}</td>
                            </tr>
                            <tr>
                              <td style={{ fontWeight: '500', color: COLORS.darkText }}>RCCM:</td>
                              <td style={{ color: COLORS.darkText }}>{selectedUser.societe.rccm || '-'}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card mb-3" style={{ 
                      border: `1px solid ${COLORS.lightBg}`,
                      borderRadius: '8px'
                    }}>
                      <div className="card-body">
                        <h6 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Informations Utilisateur</h6>
                        <table className="table table-sm table-borderless mb-0">
                          <tbody>
                            <tr>
                              <td width="40%" style={{ fontWeight: '500', color: COLORS.darkText }}>Nom:</td>
                              <td style={{ color: COLORS.darkText }}>{selectedUser.prenom} {selectedUser.nom}</td>
                            </tr>
                            <tr>
                              <td style={{ fontWeight: '500', color: COLORS.darkText }}>Email:</td>
                              <td style={{ color: COLORS.darkText }}>{selectedUser.email}</td>
                            </tr>
                            <tr>
                              <td style={{ fontWeight: '500', color: COLORS.darkText }}>Téléphone:</td>
                              <td style={{ color: COLORS.darkText }}>{selectedUser.phone || '-'}</td>
                            </tr>
                            <tr>
                              <td style={{ fontWeight: '500', color: COLORS.darkText }}>Inscription:</td>
                              <td style={{ color: COLORS.darkText }}>{new Date(selectedUser.created_at).toLocaleDateString('fr-FR')}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                
                <h6 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Documents fournis</h6>
                
                <div className="row">
                  {/* Document NIF */}
                  {selectedUser.societe.chemin_nif && (
                    <div className="col-md-6 mb-3">
                      <div className="card" style={{ 
                        border: `2px solid ${COLORS.primary}`,
                        borderRadius: '8px',
                        backgroundColor: '#E3F2FD'
                      }}>
                        <div className="card-header" style={{ 
                          backgroundColor: COLORS.primary,
                          color: 'white',
                          borderBottom: `1px solid ${COLORS.primary}`,
                          borderRadius: '6px 6px 0 0'
                        }}>
                          <i className="bi bi-file-earmark-text me-2"></i>
                          NIF - Numéro d'Identification Fiscale
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <p className="mb-1" style={{ fontWeight: '500', color: COLORS.darkText }}>Numéro NIF:</p>
                            <p style={{ color: COLORS.darkText }}>{selectedUser.societe.nif || 'Non spécifié'}</p>
                            <p className="mb-1" style={{ fontWeight: '500', color: COLORS.darkText }}>Fichier:</p>
                            <p style={{ color: COLORS.darkText }}>{getFileName(selectedUser.societe.chemin_nif)}</p>
                          </div>
                          
                          <div className="d-flex gap-2">
                            <a 
                              href={`${API_BASE_URL}/uploads/societe/${getFileName(selectedUser.societe.chemin_nif)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm fw-semibold"
                              style={{
                                color: COLORS.primary,
                                borderColor: COLORS.primary,
                                backgroundColor: 'transparent',
                                borderRadius: '6px',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = COLORS.primary;
                                e.target.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = COLORS.primary;
                              }}
                            >
                              <i className="bi bi-eye me-1"></i> Visualiser
                            </a>
                            
                            <a 
                              href={`${API_BASE_URL}/uploads/societe/${getFileName(selectedUser.societe.chemin_nif)}`}
                              download
                              className="btn btn-sm fw-semibold"
                              style={{
                                color: COLORS.darkText,
                                borderColor: '#e0e0e0',
                                backgroundColor: 'transparent',
                                borderRadius: '6px',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = COLORS.lightBg;
                                e.target.style.borderColor = COLORS.primary;
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.borderColor = '#e0e0e0';
                              }}
                            >
                              <i className="bi bi-download me-1"></i> Télécharger
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Document RCCM */}
                  {selectedUser.societe.chemin_rccm && (
                    <div className="col-md-6 mb-3">
                      <div className="card" style={{ 
                        border: `2px solid ${COLORS.success}`,
                        borderRadius: '8px',
                        backgroundColor: '#e8f5e9'
                      }}>
                        <div className="card-header" style={{ 
                          backgroundColor: COLORS.success,
                          color: 'white',
                          borderBottom: `1px solid ${COLORS.success}`,
                          borderRadius: '6px 6px 0 0'
                        }}>
                          <i className="bi bi-file-earmark-text me-2"></i>
                          RCCM - Registre du Commerce
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <p className="mb-1" style={{ fontWeight: '500', color: COLORS.darkText }}>Numéro RCCM:</p>
                            <p style={{ color: COLORS.darkText }}>{selectedUser.societe.rccm || 'Non spécifié'}</p>
                            <p className="mb-1" style={{ fontWeight: '500', color: COLORS.darkText }}>Fichier:</p>
                            <p style={{ color: COLORS.darkText }}>{getFileName(selectedUser.societe.chemin_rccm)}</p>
                          </div>
                          
                          <div className="d-flex gap-2">
                            <a 
                              href={`${API_BASE_URL}/uploads/societe/${getFileName(selectedUser.societe.chemin_rccm)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm fw-semibold"
                              style={{
                                color: '#2e7d32',
                                borderColor: '#2e7d32',
                                backgroundColor: 'transparent',
                                borderRadius: '6px',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#2e7d32';
                                e.target.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#2e7d32';
                              }}
                            >
                              <i className="bi bi-eye me-1"></i> Visualiser
                            </a>
                            
                            <a 
                              href={`${API_BASE_URL}/uploads/societe/${getFileName(selectedUser.societe.chemin_rccm)}`}
                              download
                              className="btn btn-sm fw-semibold"
                              style={{
                                color: COLORS.darkText,
                                borderColor: '#e0e0e0',
                                backgroundColor: 'transparent',
                                borderRadius: '6px',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = COLORS.lightBg;
                                e.target.style.borderColor = '#2e7d32';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.borderColor = '#e0e0e0';
                              }}
                            >
                              <i className="bi bi-download me-1"></i> Télécharger
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Message si aucun document */}
                  {!selectedUser.societe.chemin_nif && !selectedUser.societe.chemin_rccm && (
                    <div className="col-12">
                      <div className="alert text-center" style={{ 
                        backgroundColor: '#fff8e1',
                        borderColor: COLORS.warning,
                        color: '#ff8f00',
                        borderRadius: '8px',
                        borderLeft: '4px solid ' + COLORS.warning
                      }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                          width: '60px',
                          height: '60px',
                          backgroundColor: '#fff8e1',
                          color: '#ff8f00'
                        }}>
                          <i className="bi bi-exclamation-triangle fs-3"></i>
                        </div>
                        <h5 style={{ color: '#ff8f00' }}>Aucun document fourni</h5>
                        <p>Cette société n'a pas fourni de documents.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: `1px solid ${COLORS.lightBg}` }}>
                <button 
                  type="button" 
                  className="btn fw-semibold"
                  onClick={() => {
                    setShowDocumentsModal(false);
                    setSelectedUser(null);
                  }}
                  style={{
                    color: COLORS.darkText,
                    borderColor: '#e0e0e0',
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.lightBg;
                    e.target.style.borderColor = COLORS.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#e0e0e0';
                  }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour rejeter un compte */}
      {showRejectModal && selectedUser && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header" style={{ 
                backgroundColor: '#ffebee',
                borderBottom: `1px solid ${COLORS.danger}`,
                borderRadius: '12px 12px 0 0'
              }}>
                <h5 className="modal-title" style={{ color: COLORS.danger, fontWeight: '600' }}>
                  <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                    width: '30px',
                    height: '30px',
                    backgroundColor: COLORS.danger,
                    color: 'white'
                  }}>
                    <i className="bi bi-x-circle"></i>
                  </div>
                  Rejeter le compte
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedUser(null);
                    setRejectReason('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert mb-3" style={{ 
                  backgroundColor: '#fff8e1',
                  borderColor: COLORS.warning,
                  color: '#ff8f00',
                  borderRadius: '8px'
                }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <div>
                      Vous êtes sur le point de rejeter le compte de <strong style={{ color: COLORS.danger }}>{selectedUser.prenom} {selectedUser.nom}</strong>
                    </div>
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                    <i className="bi bi-chat-left-text me-1" style={{ color: COLORS.secondary }}></i>
                    Motif du rejet (obligatoire)
                  </label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Indiquez le motif du rejet..."
                    required
                    style={{
                      borderColor: '#e0e0e0',
                      color: COLORS.darkText,
                      borderRadius: '8px'
                    }}
                  ></textarea>
                  <div className="form-text mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Ce motif sera envoyé à l'utilisateur par email.
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: `1px solid ${COLORS.lightBg}` }}>
                <button 
                  type="button" 
                  className="btn fw-semibold"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedUser(null);
                    setRejectReason('');
                  }}
                  style={{
                    color: COLORS.darkText,
                    borderColor: '#e0e0e0',
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.lightBg;
                    e.target.style.borderColor = COLORS.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#e0e0e0';
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={() => handleRejectUser(selectedUser.id, `${selectedUser.prenom} ${selectedUser.nom}`)}
                  disabled={rejecting[selectedUser.id] || !rejectReason.trim()}
                  style={{
                    color: 'white',
                    backgroundColor: rejecting[selectedUser.id] || !rejectReason.trim() ? '#B0BEC5' : COLORS.danger,
                    borderColor: rejecting[selectedUser.id] || !rejectReason.trim() ? '#B0BEC5' : COLORS.danger,
                    borderRadius: '8px',
                    padding: '8px 16px',
                    transition: 'all 0.3s ease',
                    opacity: (!rejectReason.trim()) ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!rejecting[selectedUser.id] && rejectReason.trim()) {
                      e.target.style.backgroundColor = '#c62828';
                      e.target.style.borderColor = '#c62828';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!rejecting[selectedUser.id] && rejectReason.trim()) {
                      e.target.style.backgroundColor = COLORS.danger;
                      e.target.style.borderColor = COLORS.danger;
                    }
                  }}
                >
                  {rejecting[selectedUser.id] ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Traitement...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-x-circle me-1"></i>
                      Confirmer le rejet
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .alert strong,
        .table th,
        .modal-title {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-text,
        .alert,
        .table td,
        small,
        textarea {
          font-family: 'Roboto', sans-serif;
        }
        
        .table {
          border-collapse: separate;
          border-spacing: 0;
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .nav-tabs .nav-link {
          font-weight: 500;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .d-flex.justify-content-start {
            flex-wrap: wrap;
          }
          
          .btn {
            font-size: 0.875rem;
          }
          
          .modal-dialog {
            margin: 0.5rem;
          }
        }
      `}</style>
    </Layout>
  );
};

export default ActivationPage;